import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import {
  CreateServiceBriefingRequest,
  PaginatedServiceBriefings,
  ServiceBriefingApiResponse,
  ServiceBriefingAssetInput,
  ServiceBriefingAssetView,
  ServiceBriefingDetail,
  ServiceBriefingHistoryEntry,
  ServiceBriefingKind,
  ServiceBriefingListParams,
  ServiceBriefingOcOption,
  ServiceBriefingStatus,
  UpdateServiceBriefingRequest,
} from "../../types/serviceBriefing";

/** Briefings de House of Stories, House of AI y House of Digital (/api/service-briefings). */
export const serviceBriefingApi = createApi({
  reducerPath: "serviceBriefingApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ServiceBriefings", "ServiceBriefingHistory"],
  endpoints: (builder) => ({
    listServiceBriefings: builder.query<
      ServiceBriefingApiResponse<PaginatedServiceBriefings>,
      ServiceBriefingListParams
    >({
      query: (params) => {
        const search = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") return;
          if (Array.isArray(value)) {
            value.forEach((v) => search.append(key, String(v)));
          } else {
            search.append(key, String(value));
          }
        });
        const qs = search.toString();
        return `/service-briefings${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["ServiceBriefings"],
    }),

    // OC de House of Stories, AI y Digital que se pueden enlazar, con lo que rellenan
    listServiceBriefingOcOptions: builder.query<
      ServiceBriefingApiResponse<ServiceBriefingOcOption[]>,
      { search?: string; kind?: ServiceBriefingKind }
    >({
      query: ({ search, kind }) => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (kind) params.set("kind", kind);
        const qs = params.toString();
        return `/service-briefings/oc-options${qs ? `?${qs}` : ""}`;
      },
    }),

    getServiceBriefing: builder.query<
      ServiceBriefingApiResponse<ServiceBriefingDetail>,
      string
    >({
      query: (id) => `/service-briefings/${id}`,
      providesTags: ["ServiceBriefings"],
    }),

    createServiceBriefing: builder.mutation<
      ServiceBriefingApiResponse<ServiceBriefingDetail>,
      CreateServiceBriefingRequest
    >({
      query: (body) => ({ url: "/service-briefings", method: "POST", body }),
      invalidatesTags: ["ServiceBriefings", "ServiceBriefingHistory"],
    }),

    updateServiceBriefing: builder.mutation<
      ServiceBriefingApiResponse<ServiceBriefingDetail>,
      { id: string; body: UpdateServiceBriefingRequest }
    >({
      query: ({ id, body }) => ({
        url: `/service-briefings/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ServiceBriefings", "ServiceBriefingHistory"],
    }),

    updateServiceBriefingStatus: builder.mutation<
      ServiceBriefingApiResponse<ServiceBriefingDetail>,
      { id: string; status: ServiceBriefingStatus }
    >({
      query: ({ id, status }) => ({
        url: `/service-briefings/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["ServiceBriefings", "ServiceBriefingHistory"],
    }),

    addServiceBriefingAsset: builder.mutation<
      ServiceBriefingApiResponse<ServiceBriefingAssetView>,
      { id: string; body: ServiceBriefingAssetInput }
    >({
      query: ({ id, body }) => ({
        url: `/service-briefings/${id}/assets`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ServiceBriefings", "ServiceBriefingHistory"],
    }),

    deleteServiceBriefingAsset: builder.mutation<
      ServiceBriefingApiResponse<ServiceBriefingAssetView>,
      { id: string; assetId: string }
    >({
      query: ({ id, assetId }) => ({
        url: `/service-briefings/${id}/assets/${assetId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ServiceBriefings", "ServiceBriefingHistory"],
    }),

    getServiceBriefingHistory: builder.query<
      ServiceBriefingApiResponse<ServiceBriefingHistoryEntry[]>,
      string
    >({
      query: (id) => `/service-briefings/${id}/history`,
      providesTags: ["ServiceBriefingHistory"],
    }),

    deleteServiceBriefing: builder.mutation<
      { status: number; message: string },
      string
    >({
      query: (id) => ({ url: `/service-briefings/${id}`, method: "DELETE" }),
      invalidatesTags: ["ServiceBriefings"],
    }),
  }),
});

export const {
  useListServiceBriefingsQuery,
  useListServiceBriefingOcOptionsQuery,
  useGetServiceBriefingQuery,
  useCreateServiceBriefingMutation,
  useUpdateServiceBriefingMutation,
  useUpdateServiceBriefingStatusMutation,
  useAddServiceBriefingAssetMutation,
  useDeleteServiceBriefingAssetMutation,
  useGetServiceBriefingHistoryQuery,
  useDeleteServiceBriefingMutation,
} = serviceBriefingApi;
